<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { ISelectItem, Pagination } from '@/stores/core/models';
import { computed, PropType, ref, watch, watchEffect } from 'vue';
import paginatorPt from '@/presets/customizations/paginator';
import AppButton from '@/core/components/common/app-button.vue';
import AppSelect from '@/core/components/form/app-select.vue';

const emit = defineEmits(['page']);
const paginatorHeight = defineModel<Number>('paginatorHeight');

const props = defineProps({
  pagination: {
    type: Object as PropType<Pagination>,
    required: true,
  },
  includeRowsPerPage: {
    type: Boolean,
    default: true,
  },
  includePageLinks: {
    type: Boolean,
    default: false,
  },
});

const paginatorRef = ref(null);
const { height } = useElementSize(paginatorRef);

const totalPages = computed(() => {
  const total = props.pagination.rowsNumber || 0;
  const perPage = props.pagination.rowsPerPage || 6;
  return Math.ceil(total / perPage);
});

const currentPage = computed(() => {
  return props.pagination.page - 1; // convert to 0-based
});

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value + 1; // convert to 1-based
  const pages: (number | string)[] = [];
  
  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    if (current <= 4) {
      for (let i = 2; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('ellipsis-right');
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push('ellipsis-left');
      for (let i = total - 4; i <= total; i++) {
        pages.push(i);
      }
    } else {
      let groupStart: number;
      let groupEnd: number;
      
      groupStart = Math.max(current - 1, 2);
      groupEnd = Math.min(current + 1, total - 1);
      
      if (groupEnd - groupStart < 2) {
        if (groupStart === 2) {
          groupEnd = Math.min(groupStart + 2, total - 1);
        } else {
          groupStart = Math.max(groupEnd - 2, 2);
        }
      }
      
      pages.push('ellipsis-left');
      for (let i = groupStart; i <= groupEnd; i++) {
        pages.push(i);
      }
      pages.push('ellipsis-right');
      pages.push(total);
    }
  }

  return pages;
});


function onEllipsisClick(position: string) {
  const current = currentPage.value + 1;
  const total = totalPages.value;
  
  if (position === 'ellipsis-left') {
    let targetPage: number;
    
    if (current <= 4) {
      targetPage = 1;
    } else {
      targetPage = current - 2;
    }
    
    targetPage = Math.max(targetPage, 1);
    
    emit('page', { page: targetPage - 1, rows: props.pagination.rowsPerPage || 6 });
  } else if (position === 'ellipsis-right') {
    let targetPage: number;
    
    if (current <= 4) {
      targetPage = 6;
    } else {
      targetPage = current + 2;
    }
    
    targetPage = Math.min(targetPage, total);
    
    emit('page', { page: targetPage - 1, rows: props.pagination.rowsPerPage || 6 });
  }
}

const template = computed(() => {
  const pageLinks = props.includePageLinks ? 'PageLinks' : '';
  return !props.includeRowsPerPage
    ? `PrevPageLink ${pageLinks}  NextPageLink`
    : 'PrevPageLink PageLinks RowsPerPageDropdown NextPageLink';
});

watchEffect(() => (paginatorHeight.value = height.value));
</script>

<template>
  <Paginator
    ref="paginatorRef"
    @page="emit('page', $event)"
    :rowsPerPageOptions="[6, 12, 24, 48]"
    :rows="props.pagination.rowsPerPage || 6"
    :totalRecords="props.pagination.rowsNumber || 0"
    :ptOptions="{ mergeSections: true, mergeProps: true }"
    :first="(props.pagination.page - 1) * (props.pagination.rowsPerPage || 6)"
    currentPageReportTemplate="{first} - {last} of {totalRecords}"
    :template="template"
    :pt="paginatorPt"
  >
    <template #previcon>
      <app-button :outlined="false" icon="hugeicons:arrow-left-01" label="core.pagination.previous" :severity="'regular'" />
    </template>

    <template #nexticon>
      <app-button
        :outlined="false"
        class="pt-1"
        icon="hugeicons:arrow-right-01"
        label="core.pagination.next"
        :severity="'regular'"
        iconPos="right"
      />
    </template>

    <template #container="{ page, pageCount, prevPageCallback, nextPageCallback }">
      <div class="flex items-center gap-4 bg-transparent w-full py-1 justify-between">
        <Button
          icon="pi pi-arrow-left"
          :label="$t('core.pagination.previous')"
          rounded
          text
          @click="prevPageCallback"
          :disabled="page === 0"
          :pt-options="{ mergeProps: true, mergeSections: true }"
          :pt="{ icon: '!mr-[3px]' }"
        />

        <div class="flex items-center gap-1" v-if="!!pageCount">
          <template v-for="(i, index) in visiblePages" :key="index">
            <div
              v-if="typeof i === 'number'"
              class="w-[24px] h-[24px] p-5 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300"
              :class="{
                'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700': i - 1 !== currentPage,
                '!bg-gray-100 dark:!bg-gray-700 text-gray-800 dark:text-gray-100': i - 1 === currentPage,
              }"
              @click="$emit('page', { page: i - 1, rows: props.pagination.rowsPerPage || 6 })"
            >
              {{ i }}
            </div>
            <div v-else class="w-[24px] h-[24px] p-5 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 text-sm font-medium" @click="onEllipsisClick(i)">...</div>
          </template>

          <app-select v-if="props.includeRowsPerPage"
            :model-value="props.pagination.rowsPerPage"
            class="ml-2"
            @update:model-value="$emit('page', { page: 0, rows: $event || 6 })"
            option-value="value"
            :options="[
              { value: 6, label: '6' },
              { value: 12, label: '12' },
              { value: 24, label: '24' },
              { value: 48, label: '48' },
            ] as ISelectItem[]"
            use-small-rounding
          />
        </div>

        <Button
          icon="pi pi-arrow-right"
          iconPos="right"
          :label="$t('core.pagination.next')"
          rounded
          text
          @click="nextPageCallback"
          :disabled="!pageCount || page === pageCount - 1"
          :pt-options="{ mergeProps: true, mergeSections: true }"
          :pt="{ icon: '!ml-[3px]' }"
        />
      </div>
    </template>
  </Paginator>
</template>

<style>
.app-paginator-content > div {
  box-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width)) var(--tw-ring-color) !important;
}

.app-paginator-content > div:focus-within {
  box-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width)) var(--tw-ring-color) !important;
}
</style>

<style>
.app-paginator-content > div {
  box-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width)) var(--tw-ring-color) !important;
}

.app-paginator-content > div:focus-within {
  box-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width)) var(--tw-ring-color) !important;
}
</style>
